import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { CarMakerService } from 'src/app/services/car-maker/car-maker.service';
import { CarMakerModalService } from 'src/app/services/car-maker-modal/car-maker-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-car-makers',
  templateUrl: './add-edit-car-makers.component.html',
  styleUrls: ['./add-edit-car-makers.component.scss']
})
export class AddEditCarMakersComponent extends DestructorComponent implements OnInit {

  @Output() carMakerAdded = new EventEmitter<void>();
  @Output() carMakerUpdated = new EventEmitter<void>();

  carMakerForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedCarMaker: any = null;

  constructor(
    private fb: FormBuilder,
    private _carMakerService: CarMakerService,
    private _carMakerModalService: CarMakerModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.carMakerForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]]
    });
  }

  // Getter methods for form controls
  get name() { return this.carMakerForm.get('name'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_car_maker_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceCarMaker = this._carMakerModalService.getSelectedCarMaker();
        if (serviceCarMaker) {
          this.isEditMode = true;
          this.selectedCarMaker = serviceCarMaker;
          this.populateForm(this.selectedCarMaker);
        } else {
          this.isEditMode = false;
          this.selectedCarMaker = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._carMakerModalService.clearSelectedCarMaker();
        this.resetForm();
      });
    }
  }

  private populateForm(carMaker: any): void {
    this.carMakerForm.patchValue({
      name: carMaker.name || ''
    });
  }

  private resetForm(): void {
    this.carMakerForm.reset();
    this.isLoading = false;
    this.markFormGroupUntouched(this.carMakerForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_car_maker_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.carMakerForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.carMakerForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      const apiCall = this.isEditMode
        ? this._carMakerService.UpdateData(this.selectedCarMaker._id, submitData)
        : this._carMakerService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Car maker operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "Car maker has been successfully updated!"
                : "Car maker has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.carMakerUpdated.emit();
                } else {
                  this.carMakerAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Car maker operation failed:', error);

            // Show error message
            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.carMakerForm);
    }
  }
}
